import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Mail, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { verifyOtp, resendOtp } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "./AuthShell";

export default function VerifyOtp() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const { loginAs } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!email) navigate("/auth");
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await verifyOtp({ email, code });
      // New flow: server returns either a setup_token (password setup required)
      // or an access_token (legacy returning user).
      if (res.data?.needs_password_setup) {
        navigate(`/set-password?email=${encodeURIComponent(email)}&token=${encodeURIComponent(res.data.setup_token)}`);
        return;
      }
      loginAs(res.data.access_token, res.data.user);
      toast.success("Welcome to Sabzy AI");
      navigate("/chat");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendOtp({ email });
      toast.success("New code sent");
      setCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell>
      <div className="mb-8 flex items-center justify-between">
        <Logo />
        <ThemeToggle />
      </div>

      <div className="mb-8">
        <div className="mb-2 text-xs uppercase tracking-[0.25em] text-primary">Step 2 of 2</div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Verify email.</h1>
        <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Mail className="h-4 w-4" /> Code sent to <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="verify-form">
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
            <InputOTPGroup>
              <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
              <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <Button type="submit" disabled={loading || code.length !== 6} className="h-11 w-full rounded-lg" data-testid="verify-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & enter <ArrowRight className="ml-2 h-4 w-4" /></>}
        </Button>
      </form>

      <div className="mt-8 text-center text-sm text-muted-foreground">
        Didn't get a code?{" "}
        <button
          type="button"
          disabled={resending || cooldown > 0}
          onClick={handleResend}
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="resend-otp-btn"
        >
          <RotateCcw className="h-3 w-3" />
          {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending..." : "Resend"}
        </button>
      </div>
    </AuthShell>
  );
}
